// Constants
import actionsList from "@Constants/actionsList";

// Motivators Common
import {
	generateAutoAlertsSetupTree,
	generateCommissionCycleTree2Levels,
	generateCommissionCycleTreeTempGuaranteed,
	generateSpecialUnitsListTree,
	generateTrainingCommissionCycleTree,
	updateSelectedItem,
	updateSelectedItemAutoAlert,
	updateSelectedItemAwards,
	updateSelectedItemSpecialUnits,
	updateSelectedItemTemporaryGuaranteed,
} from "./MotivatorsCommon.jsx";

import { getSpiffSetupTree } from "@Motivators/api/spiffs";

// Awards API
import {
	getSpiffAwards,
	getSpiffAwardsTree,
	onCreateSaleSpiffAward,
	onUpdateSaleSpiffAward,
	onDeleteSaleSpiffAward,
	onCreateServiceSpiffAward,
	onUpdateServiceSpiffAward,
	onDeleteServiceSpiffAward,
} from "@/Motivators/api/awards";

// Spiff
import {
	createSpiffItem,
	// Mutations
	createSpiffSetup,
	deleteSpiffItems,
	deleteSpiffSetup,
	getCommissionCyclesSpiffs,
	// Queries
	getSpiffSetup,
	onCreateSpiffItem,
	// Subscriptions
	onCreateSpiffSetup,
	onDeleteSpiffItems,
	onDeleteSpiffSetup,
	onUpdateSpiffItem,
	onUpdateSpiffSetup,
	updateSpiffEffectiveDate,
	updateSpiffItem,
	updateSpiffSetup,
	updateSpiffSetupTitle,
} from "@Motivators/api/spiffs";

// Auto Alerts
import {
	addAutoAlertItem,
	// Mutations
	addAutoAlertsSetup,
	deleteAutoAlertItems,
	deleteAutoAlertsSetup,
	// Queries
	getAutoAlertsSetupsById,
	getCommissionCyclesAutoAlertsSetup,
	onAddAutoAlertItem,
	// Subscriptions
	onCreateAutoAlertsSetup,
	onDeleteAutoAlertItems,
	onDeleteAutoAlertsSetup,
	onUpdateAutoAlertItem,
	onUpdateAutoAlertsSetup,
	updateAutoAlertItem,
	updateAutoAlertsEffectiveDate,
	updateAutoAlertsSetup,
	updateAutoAlertsSetupTitle,
} from "@Motivators/api/autoAlerts";

// Temporary Guaranteed
import {
	createTemporaryGuaranteedItem,
	createTemporaryGuaranteedSetup,
	deleteTemporaryGuaranteedItems,
	deleteTemporaryGuaranteedSetup,
	getCommissionCyclesTemporaryGuaranteed,
	getTemporaryGuaranteedById,
	// Subscriptions
	onCreateTemporaryGuaranteedItem,
	onCreateTemporaryGuaranteedSetup,
	onDeleteTemporaryGuaranteedItem,
	onDeleteTemporaryGuaranteedSetup,
	onUpdateTemporaryGuaranteedItem,
	onUpdateTemporaryGuaranteedSetup,
	updateTemporaryGuaranteedEffectiveDate,
	updateTemporaryGuaranteedItem,
	updateTemporaryGuaranteedTitle,
	updateTemporaryGuarateedSetup,
} from "@Motivators/api/temporatyGuaranteed";

// Training
import {
	// Queries
	getTraining,
	getTrainingSideBar,
	onCreateTrainingItem,
	onDeleteTrainingItems,
	onUpdateTrainingItem,
} from "@Motivators/api/training";

// Special Units
import {
	addNewSpecialUnitToList,
	createSpecialUnitsSetup,
	deleteSpecialUnitItem,
	deleteSpecialUnitsSetup,
	getCommissionCyclesSpecialUnitsList,
	getSpecialUnitListById,
	onCreateSpecialUnitsItem,
	// New subscriptions.
	onCreateSpecialUnitsSetup,
	onDeleteSpecialUnitsItem,
	onDeleteSpecialUnitsSetup,
	onUpdateSpecialUnitsSetup,
	updateSpecialUnistRule,
	updateSpecialUnitEffectiveDate,
	updateSpecialUnitTitle,
} from "@Motivators/api/specialUnits";
import { extractLeafKeys, getLeafKey } from "@/Common/GenericTree/utils";

const resolveDefinitions = () => {
	// Definitions
	const actions = {
		editable: "",
	};

	const identifiers = {
		title: "",
		department: "",
		subscriptionName: "",
		queryKey: "",
		sideBarListProp: "",
		sideBarIdProp: "",
		effectiveDateFromFieldName: "",
		effectiveDateToFieldName: "",
		createdBy: "",
		createdOn: "",
		lastModifiedBy: "",
		lastModifiedOn: "",
	};

	const queries = {
		getSideBarData: async () => {},
		getBodyData: async () => {},
	};

	const mutations = {
		createSetup: async () => {},
		updateSetup: async () => {},
		updateEffectiveDateSetup: async () => {},
		updateTitleSetup: async () => {},
		deleteSetup: async () => {},
		createItem: async () => {},
		updateItem: async () => {},
		deleteItem: async () => {},
		updateSetupTitle: async () => {},
		updateEffectiveDate: async () => {},
	};

	const subscriptions = new Map([
		["onCreateSetup", { query: "", getter: "" }],
		["onUpdateSetup", { query: "", getter: "" }],
		["onDeleteSetup", { query: "", getter: "" }],
		["onCreateItem", { query: "", getter: "" }],
		["onUpdateItem", { query: "", getter: "" }],
		["onDeleteItem", { query: "", getter: "" }],
		["onCreateSubItem", { query: "", getter: "" }],
		["onUpdateSubItem", { query: "", getter: "" }],
		["onDeleteSubItem", { query: "", getter: "" }],
	]);

	const helpers = {
		generateTree: () => {},
	};

	return {
		actions,
		queries,
		mutations,
		subscriptions,
		identifiers,
		helpers,
	};
};

/**
 * useMotivatorsQueriesModel - This function is used to return the correct queries, mutations, subscriptions.ts, and helpers based on the motivator type.
 * @param motivatorType
 * @return {Object}
 */
export const useMotivatorsQueriesModel = (motivatorType) => {
	const { actions, queries, mutations, subscriptions, identifiers, helpers } = resolveDefinitions();

	const setupMap = new Map([
		["autoAlertsSetup", () => configureAutoAlertsSetup({ actions, queries, mutations, subscriptions, identifiers, helpers })],
		["specialUnitsSetup", () => configureSpecialUnits({ actions, queries, mutations, subscriptions, identifiers, helpers })],
		["spiffSetup", () => configureSpiffSetup({ actions, queries, mutations, subscriptions, identifiers, helpers }, "spiffSetup")],
		[
			"spiffBonusSetup",
			() => configureSpiffSetup({ actions, queries, mutations, subscriptions, identifiers, helpers }, "spiffBonusSetup"),
		],
		["spiffAwards", () => configureSpiffAwards({ actions, queries, mutations, motivatorType, subscriptions, identifiers, helpers })],
		["serviceTraining", () => configureServiceTraining({ actions, queries, mutations, subscriptions, identifiers, helpers })],
		[
			"temporaryGuaranteed",
			() =>
				configureTemporaryGuaranteed({
					actions,
					queries,
					mutations,
					subscriptions,
					identifiers,
					helpers,
					department: "service",
				}),
		],
		[
			"salesTemporaryGuaranteed",
			() =>
				configureTemporaryGuaranteed({
					actions,
					queries,
					mutations,
					subscriptions,
					identifiers,
					helpers,
					department: "sales",
				}),
		],
		[
			"spiffBonusAwards",
			() => configureSpiffAwards({ actions, queries, mutations, motivatorType, subscriptions, identifiers, helpers }),
		],
	]);

	// Execute configuration based on motivatorType
	const setupFunction = setupMap.get(motivatorType);
	if (setupFunction) {
		setupFunction();
	}

	return { actions, queries, mutations, subscriptions, identifiers, helpers };
};

const configureAutoAlertsSetup = ({ actions, queries, mutations, subscriptions, identifiers, helpers }) => {
	// Actions
	actions.editable = actionsList.EDIT_OPPORTUNITY_MINING;

	// Identifiers
	identifiers.title = "New Opportunity Mining Setup";
	identifiers.effectiveDateFromFieldName = "alertDate";
	identifiers.createdBy = "createdBy";
	identifiers.createdOn = "createdOn";
	identifiers.lastModifiedBy = "lastModifiedBy";
	identifiers.lastModifiedOn = "lastModifiedOn";
	identifiers.attribute = "autoAlertsSetups";

	// Queries
	queries.getSideBarData = getCommissionCyclesAutoAlertsSetup;
	queries.getBodyData = getAutoAlertsSetupsById;

	// Helpers
	helpers.generateTree = generateAutoAlertsSetupTree;
	helpers.updateSelectedItem = updateSelectedItemAutoAlert;

	// Mutations
	mutations.createSetup = addAutoAlertsSetup;
	mutations.deleteSetup = deleteAutoAlertsSetup;
	mutations.updateSetup = updateAutoAlertsSetup;
	mutations.updateSetupTitle = updateAutoAlertsSetupTitle;
	mutations.updateEffectiveDate = updateAutoAlertsEffectiveDate;
	mutations.createItem = addAutoAlertItem;
	mutations.updateItem = updateAutoAlertItem;
	mutations.deleteItem = deleteAutoAlertItems;

	// Subscriptions
	subscriptions.set("onCreateSetup", {
		query: onCreateAutoAlertsSetup,
		getter: "onCreateAutoAlertSetup",
	});
	subscriptions.set("onDeleteSetup", {
		query: onDeleteAutoAlertsSetup,
		getter: "onDeleteAutoAlertsSetup",
	});
	subscriptions.set("onUpdateSetup", {
		query: onUpdateAutoAlertsSetup,
		getter: "onUpdateAutoAlertsSetup",
	});
	subscriptions.set("onCreateItem", {
		query: onAddAutoAlertItem,
		getter: "onAddAutoAlertItem",
	});
	subscriptions.set("onDeleteItem", {
		query: onDeleteAutoAlertItems,
		getter: "onDeleteAutoAlertItems",
	});
	subscriptions.set("onUpdateItem", {
		query: onUpdateAutoAlertItem,
		getter: "onUpdateAutoAlertItem",
	});
};

const configureSpiffSetup = ({ actions, queries, mutations, subscriptions, identifiers, helpers }, motivatorType) => {
	const isSalesSetup = motivatorType === "spiffSetup";
	const queryKey = isSalesSetup ? "spiffSetupEntry" : "spiffBonusSetupEntry";

	// Identifiers
	identifiers.department = isSalesSetup ? "sales" : "service";
	identifiers.title = "New Spiff Setup";
	identifiers.createdBy = "createdBy";
	identifiers.createdOn = "createdAt";
	identifiers.lastModifiedBy = "lastModifiedBy";
	identifiers.lastModifiedOn = "lastModifiedOn";
	identifiers.attribute = "spiffs";
	identifiers.effectiveDateFromFieldName = "effectiveDateRangeFrom";
	identifiers.effectiveDateToFieldName = "effectiveDateRangeTo";

	// Actions
	actions.editable = isSalesSetup ? actionsList.EDIT_SALES_SPIFF_DEFINITIONS : actionsList.EDIT_SERVICE_SPIFF_DEFINITIONS;
	// Queries
	queries.getSideBarData = async ({ locationId, department }) => {
		return await getSpiffSetupTree({ locationId, department });
	};
	queries.getBodyData = getSpiffSetup;

	// Mutations
	mutations.createSetup = createSpiffSetup;
	mutations.deleteSetup = deleteSpiffSetup;
	mutations.updateSetup = updateSpiffSetup;
	mutations.updateSetupTitle = updateSpiffSetupTitle;
	mutations.updateEffectiveDate = updateSpiffEffectiveDate;
	mutations.createItem = createSpiffItem;
	mutations.updateItem = updateSpiffItem;
	mutations.deleteItem = deleteSpiffItems;

	// Subscriptions
	subscriptions.set("onCreateSetup", {
		query: onCreateSpiffSetup,
		getter: "onCreateSpiffSetup",
	});

	subscriptions.set("onUpdateSetup", {
		query: onUpdateSpiffSetup,
		getter: "onUpdateSpiffSetup",
	});

	subscriptions.set("onDeleteSetup", {
		query: onDeleteSpiffSetup,
		getter: "onDeleteSpiffSetup",
	});

	subscriptions.set("onCreateItem", {
		query: onCreateSpiffItem,
		getter: "onCreateSpiffItem",
	});

	subscriptions.set("onUpdateItem", {
		query: onUpdateSpiffItem,
		getter: "onUpdateSpiffItem",
	});

	subscriptions.set("onDeleteItem", {
		query: onDeleteSpiffItems,
		getter: "onDeleteSpiffItems",
	});

	// Helpers
	helpers.generateTree = ({ data }) => {
		return data?.tree || [];
	};

	helpers.getDefaultSelection = ({ data }) => {
		if (!data?.hints?.defaultSelectedIds?.length) return null;
		return getLeafKey(data.hints.defaultSelectedIds);
	};

	helpers.getExpandedIds = ({ data }) => {
		if (!data?.hints?.expandedIds?.length) return [];
		const [defaultExpanded] = data.hints.expandedIds;
		return defaultExpanded;
	};

	helpers.prefetchData = async ({ data, locationId, department }) => {
		if (!data?.hints?.prefetchIds?.length) return { cacheUpdates: [], defaultSelection: null };

		const prefetchPromises = data.hints.prefetchIds.map(async (path) => {
			const [commissionCycleId, spiffSetupId] = path;
			const setupData = await getSpiffSetup({ locationId, department, spiffId: spiffSetupId });
			return { spiffSetupId, setupData };
		});

		const results = await Promise.all(prefetchPromises);
		const cacheUpdates = results.map(({ spiffSetupId, setupData }) => ({
			queryKey: [queryKey, spiffSetupId, locationId],
			data: setupData,
		}));

		let defaultSelection = null;
		if (data?.hints?.defaultSelectedIds?.length) {
			defaultSelection = getLeafKey(data.hints.defaultSelectedIds);
			if (defaultSelection) {
				const [cycleId, setupId] = extractLeafKeys(defaultSelection);
				defaultSelection = {
					_id: defaultSelection,
					commissionCycleId: cycleId,
					spiffSetupId: setupId,
				};
			}
		}

		return { cacheUpdates, defaultSelection };
	};

};

const configureSpiffAwards = ({ actions, queries, subscriptions, motivatorType, identifiers, helpers }) => {
	// Identifiers
	identifiers.department = motivatorType === "spiffAwards" ? "sales" : "service";
	identifiers.createdBy = "createdBy";
	identifiers.createdOn = "createdAt";
	identifiers.lastModifiedBy = "updatedBy";
	identifiers.lastModifiedOn = "updatedAt";

	// Actions
	actions.editable = motivatorType === "spiffAwards" ? actionsList.EDIT_SALES_SPIFF_AWARDS : actionsList.EDIT_SERVICE_SPIFF_AWARDS;

	// Queries
	queries.getSideBarData = async ({ locationId, department }) => {
		return await getSpiffAwardsTree({ locationId, department });
	};
	queries.getBodyData = getSpiffAwards;

	// Helpers
	helpers.generateTree = ({ data }) => {
		return data?.tree || [];
	};

	helpers.getDefaultSelection = ({ data }) => {
		if (!data?.hints?.defaultSelectedIds?.length) return null;
		return getLeafKey(data.hints.defaultSelectedIds);
	};

	helpers.getExpandedIds = ({ data }) => {
		if (!data?.hints?.expandedIds?.length) return [];
		const [defaultExpanded] = data.hints.expandedIds;
		return defaultExpanded;
	};

	helpers.prefetchData = async ({ data, locationId, department }) => {
		if (!data?.hints?.prefetchIds?.length) return { cacheUpdates: [], defaultSelection: null };

		const prefetchPromises = data.hints.prefetchIds.map(async (path) => {
			const [commissionCycleId, spiffSetupId, spiffItemId] = path;
			const awardData = await getSpiffAwards({ locationId, department, spiffItemId });
			return { spiffItemId, awardData };
		});

		const results = await Promise.all(prefetchPromises);
		const cacheUpdates = results.map(({ spiffItemId, awardData }) => ({
			queryKey: [`getSpiffAwards`, locationId, spiffItemId],
			data: awardData,
		}));

		let defaultSelection = null;
		if (data?.hints?.defaultSelectedIds?.length) {
			defaultSelection = getLeafKey(data.hints.defaultSelectedIds);
			if (defaultSelection) {
				const [cycleId, setupId, itemId] = extractLeafKeys(defaultSelection);
				defaultSelection = {
					_id: defaultSelection,
					commissionCycleId: cycleId,
					spiffSetupId: setupId,
					spiffItemId: itemId,
				};
			}
		}

		return { cacheUpdates, defaultSelection };
	};
};

const configureServiceTraining = ({ actions, queries, subscriptions, helpers }) => {
	// Identifiers

	// Actions
	actions.editable = actionsList.EDIT_SERVICES_TRAINING;

	// Queries
	queries.getSideBarData = getTrainingSideBar;
	queries.getBodyData = getTraining;

	// Helpers
	helpers.generateTree = generateTrainingCommissionCycleTree;

	// set used subs
	subscriptions.set("onCreateItem", {
		query: onCreateTrainingItem,
		getter: "onCreateTrainingItem",
	});

	subscriptions.set("onUpdateItem", {
		query: onUpdateTrainingItem,
		getter: "onUpdateTrainingItem",
	});

	subscriptions.set("onDeleteItem", {
		query: onDeleteTrainingItems,
		getter: "onDeleteTrainingItems",
	});
};

const configureSpecialUnits = ({ actions, queries, mutations, subscriptions, identifiers, helpers }) => {
	// Identifiers
	identifiers.title = "New List";
	identifiers.createdBy = "createdBy";
	identifiers.createdOn = "createdOn";
	identifiers.lastModifiedBy = "lastModifiedBy";
	identifiers.lastModifiedOn = "lastModifiedOn";
	identifiers.attribute = "specialUnitsList";

	helpers.generateTree = generateSpecialUnitsListTree;
	helpers.updateSelectedItem = updateSelectedItemSpecialUnits;

	// Actions
	actions.editable = actionsList.EDIT_SPECIAL_UNIT_LISTS;

	// Queries
	queries.getSideBarData = getCommissionCyclesSpecialUnitsList;
	queries.getBodyData = getSpecialUnitListById;

	// Mutations
	mutations.updateEffectiveDate = updateSpecialUnitEffectiveDate;
	mutations.updateSetupTitle = updateSpecialUnitTitle;
	mutations.updateRules = updateSpecialUnistRule;
	mutations.createItem = addNewSpecialUnitToList;
	mutations.deleteItem = deleteSpecialUnitItem;
	mutations.createSetup = createSpecialUnitsSetup;
	mutations.deleteSetup = deleteSpecialUnitsSetup;

	// Subscriptions
	subscriptions.set("onCreateSetup", {
		query: onCreateSpecialUnitsSetup,
		getter: "onCreateSpecialUnitsList",
	});

	subscriptions.set("onDeleteSetup", {
		query: onDeleteSpecialUnitsSetup,
		getter: "onDeleteSpecialUnitsList",
	});

	subscriptions.set("onUpdateSetup", {
		query: onUpdateSpecialUnitsSetup,
		getter: "onUpdateSpecialUnitsList",
	});

	subscriptions.set("onCreateItem", {
		query: onCreateSpecialUnitsItem,
		getter: "onCreateSpecialUnitsItem",
	});

	subscriptions.set("onDeleteItem", {
		query: onDeleteSpecialUnitsItem,
		getter: "onDeleteSpecialUnitsItem",
	});
};

const configureTemporaryGuaranteed = ({ actions, queries, mutations, subscriptions, identifiers, helpers, department }) => {
	// Identifiers
	identifiers.title = "New List";
	identifiers.effectiveDateFromFieldName = "effectiveDateFromItem";
	identifiers.effectiveDateToFieldName = "effectiveDateToItem";
	identifiers.createdBy = "createdBy";
	identifiers.createdOn = "createdOn";
	identifiers.lastModifiedBy = "lastModifiedBy";
	identifiers.lastModifiedOn = "lastModifiedOn";
	identifiers.attribute = "temporaryGuaranties";
	identifiers.department = department;

	helpers.generateTree = generateCommissionCycleTreeTempGuaranteed;
	helpers.updateSelectedItem = updateSelectedItemTemporaryGuaranteed;

	// Actions
	actions.editable = actionsList.EDIT_TEMP_GUARANTEED_SETUP;

	// Queries
	queries.getSideBarData = getCommissionCyclesTemporaryGuaranteed;
	queries.getBodyData = getTemporaryGuaranteedById;

	// Mutations
	mutations.createSetup = createTemporaryGuaranteedSetup;
	mutations.deleteSetup = deleteTemporaryGuaranteedSetup;
	mutations.updateSetupTitle = updateTemporaryGuaranteedTitle;
	mutations.updateSetup = updateTemporaryGuarateedSetup;
	mutations.updateEffectiveDate = updateTemporaryGuaranteedEffectiveDate;
	mutations.createItem = createTemporaryGuaranteedItem;
	mutations.updateItem = updateTemporaryGuaranteedItem;
	mutations.deleteItem = deleteTemporaryGuaranteedItems;

	// // Subscriptions
	subscriptions.set("onCreateSetup", {
		query: onCreateTemporaryGuaranteedSetup,
		getter: "onCreateTemporaryGuaranteedSetup",
	});

	subscriptions.set("onDeleteSetup", {
		query: onDeleteTemporaryGuaranteedSetup,
		getter: "onDeleteTemporaryGuaranteedSetup",
	});

	subscriptions.set("onUpdateSetup", {
		query: onUpdateTemporaryGuaranteedSetup,
		getter: "onUpdateTemporaryGuaranteedSetup",
	});

	subscriptions.set("onCreateItem", {
		query: onCreateTemporaryGuaranteedItem,
		getter: "onCreateTemporaryGuaranteedItem",
	});

	subscriptions.set("onUpdateItem", {
		query: onUpdateTemporaryGuaranteedItem,
		getter: "onUpdateTemporaryGuaranteedItem",
	});

	subscriptions.set("onDeleteItem", {
		query: onDeleteTemporaryGuaranteedItem,
		getter: "onDeleteTemporaryGuaranteedItem",
	});
};
